import { Box, Divider, Paper, Skeleton } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import dayjs from 'dayjs';
import { getMemberChangeLog } from 'src/shared/services/memberChangeLogService';
import { MemberChangeLog } from 'src/modules/member/models/memberChangeLog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { groupLogsByDate } from 'src/shared/functions/groupLogsByDate';
import { HeaderSubTitle } from '../atoms/headerSubTitle';
import { DashboardLogItem } from '../atoms/dashboardLogItem';

type MemberDashboardLogProps = {
  associationId: number | undefined;
  districtId: number | undefined;
};

export const MemberDashboardLog = (props: MemberDashboardLogProps) => {
  const { associationId, districtId } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>();
  const [groupedLogs, setGroupedLogs] = useState<{ [key: string]: MemberChangeLog[] }>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMemberChangeLog(0, 10, 'changeLog_id', 'desc', associationId, districtId);
      if (isMounted()) {
        setGroupedLogs(groupLogsByDate(res.data.results.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)))));
        ConsoleHelper.log('RES CahngeLog', res.data.results);
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [associationId, districtId, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedKeys = [
    ...(groupedLogs.Idag && groupedLogs.Idag.length > 0 ? [t('dashboard:today')] : []),
    ...(groupedLogs[t('dashboard:yesterday')] && groupedLogs[t('dashboard:yesterday')].length > 0 ? [t('dashboard:yesterday')] : []),
    ...Object.keys(groupedLogs)
      .filter((key) => key !== t('dashboard:today') && key !== t('dashboard:yesterday'))
      .sort((a, b) => dayjs(b, 'D MMM').diff(dayjs(a, 'D MMM'))),
  ];

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <HeaderSubTitle label={t('dashboard:lastSystemEvents')} />
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {[...Array(6)].map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index}>
              <Skeleton variant='rectangular' animation='wave' sx={{ height: '30px', width: '40%', mb: 1, borderRadius: '4px' }} />
              <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '100%', mb: 1 }} />
              <Skeleton variant='text' animation='wave' sx={{ height: '20px', width: '80%', mb: 2 }} />
              <Skeleton variant='rectangular' animation='wave' sx={{ height: '20px', width: '30%', borderRadius: '4px' }} />
              {index < 5 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}
        </Box>
      )}
      {!loading &&
        sortedKeys.length > 0 &&
        sortedKeys.map((key) => (
          <Box key={key} mt={2}>
            <Box>
              <HeaderSubTitle label={key} colorTextSecondary />
            </Box>
            <Box display='flex' flexDirection='column'>
              {groupedLogs[key]?.map((logItem) => (
                <DashboardLogItem key={logItem.changeLog_id} item={logItem} />
              ))}
            </Box>
          </Box>
        ))}

      {!loading && sortedKeys.length === 0 && (
        <Box mt={2}>
          <Box>
            <em>
              <HeaderSubTitle label={t('dashboard:noEvents')} colorTextSecondary />
            </em>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
